/**
 * The Riley rail's transport over Patchbay's /api/v1 proxy (never Riley directly): the user's session cookie and the
 * CSRF header on every POST. Riley's service token never reaches the browser, and the user's Desk token never
 * reaches Riley (hard rule 4). Owner: rw-web-wiring (W1.5), replacing the fixture-only rail.
 *
 * - `POST /chat/start`, `POST /chat/{id}/messages` (text/event-stream when Riley streams, JSON otherwise).
 * - `GET /chat/{id}`: the stored thread, polled when a streamed answer was lost (patchbay#196).
 * - `POST /cards/{id}/undismiss`: Undo dismiss (patchbay#198).
 * - `POST /cards/{id}/confirm` with `edited_payload` and `acknowledged_warnings` (warn-then-allow, round 7).
 * - `POST /cases/{id}/investigate`, streamed as a Trace.
 * - `POST /riley/feedback`: thumbs up/down on a message, card or investigation (an eval row; patchbay#163).
 */
import { currentCsrfToken } from '../../lib/api/client'
import type {
  ChatDelivery,
  InvestigationStreamEvent,
  RileyCard,
  RileyCardProgress,
  RileyChatStreamEvent,
  RileyChatThread,
  RileyRailDataSource,
  RileyRating,
  StoredInvestigation,
} from './contracts'
import { parseSseStream } from './sse'
import { announceWalkthrough } from './walkthrough-event'

const POST_TIMEOUT_MS = 30_000
const STREAM_TIMEOUT_MS = 240_000 // investigate caps at 3 min server-side; the stream gets a little longer

/** A non-2xx answer with the API's `{code, detail}` and any extra fields (e.g. warnings to acknowledge). */
export class RileyApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
    readonly body: Record<string, unknown> = {},
  ) {
    super(message)
    this.name = 'RileyApiError'
  }
}

export interface RileyHttpOptions {
  base?: string
  fetchImpl?: typeof fetch
}

export function createHttpRileySource({ base = '', fetchImpl }: RileyHttpOptions = {}): RileyRailDataSource {
  const doFetch = (...args: Parameters<typeof fetch>) => (fetchImpl ?? globalThis.fetch)(...args)

  async function post(path: string, body: unknown, { accept = 'application/json', timeout = POST_TIMEOUT_MS } = {}) {
    const ctl = new AbortController()
    const timer = setTimeout(() => ctl.abort(new DOMException('timeout', 'TimeoutError')), timeout)
    const headers: Record<string, string> = { Accept: accept, 'Content-Type': 'application/json' }
    const csrf = currentCsrfToken()
    if (csrf) headers['X-CSRF-Token'] = csrf
    try {
      const res = await doFetch(`${base}${path}`, {
        method: 'POST',
        headers,
        credentials: 'same-origin',
        body: JSON.stringify(body ?? {}),
        signal: ctl.signal,
      })
      if (!res.ok) {
        const data = (await res.json().catch(() => ({}))) as Record<string, unknown>
        throw new RileyApiError(res.status, String(data.code ?? `http_${res.status}`), String(data.detail ?? res.statusText), data)
      }
      return { res, done: () => clearTimeout(timer) }
    } catch (error) {
      clearTimeout(timer)
      throw error
    }
  }

  async function postJson<T>(path: string, body: unknown): Promise<T> {
    const { res, done } = await post(path, body)
    try {
      return (await res.json()) as T
    } finally {
      done()
    }
  }

  async function getJson<T>(path: string): Promise<T> {
    const ctl = new AbortController()
    const timer = setTimeout(() => ctl.abort(new DOMException('timeout', 'TimeoutError')), POST_TIMEOUT_MS)
    try {
      const res = await doFetch(`${base}${path}`, {
        method: 'GET',
        headers: { Accept: 'application/json' },
        credentials: 'same-origin',
        signal: ctl.signal,
      })
      const data = (await res.json().catch(() => ({}))) as Record<string, unknown>
      if (!res.ok)
        throw new RileyApiError(res.status, String(data.code ?? `http_${res.status}`), String(data.detail ?? res.statusText), data)
      return data as T
    } finally {
      clearTimeout(timer)
    }
  }

  async function* streamOf<T>(res: Response, done: () => void): AsyncGenerator<T> {
    try {
      if (!res.body) return
      for await (const frame of parseSseStream(res.body)) yield { event: frame.event, data: frame.data } as T
    } finally {
      done()
    }
  }

  return {
    startChat: (input) => postJson<RileyChatThread>('/api/v1/chat/start', input),
    async postMessage(threadId, input): Promise<ChatDelivery> {
      const { res, done } = await post(`/api/v1/chat/${encodeURIComponent(threadId)}/messages`, input, {
        accept: 'text/event-stream, application/json',
        timeout: STREAM_TIMEOUT_MS,
      })
      if ((res.headers.get('content-type') ?? '').includes('text/event-stream')) {
        return streamOf<RileyChatStreamEvent>(res, done)
      }
      try {
        return (await res.json()) as RileyChatThread
      } finally {
        done()
      }
    },
    async confirmCard(cardId, editedPayload, acknowledged) {
      const card = await postJson<RileyCard>(`/api/v1/cards/${encodeURIComponent(cardId)}/confirm`, {
        edited_payload: editedPayload ?? null,
        acknowledged_warnings: acknowledged ?? [],
      })
      announceWalkthrough()
      return card
    },
    tidyServiceHistory: (cardId, text) =>
      postJson<{ text: string; stripped?: string[] }>(`/api/v1/cards/${encodeURIComponent(cardId)}/tidy-service-history`, { text }),
    getChat: (threadId) => getJson<RileyChatThread>(`/api/v1/chat/${encodeURIComponent(threadId)}`),
    undismissCard: (cardId) => postJson<RileyCard>(`/api/v1/cards/${encodeURIComponent(cardId)}/undismiss`, {}),
    undoBulkCard: (cardId) => postJson<RileyCard>(`/api/v1/queue/bulk/${encodeURIComponent(cardId)}/undo`, {}),
    undoTriageCard: (cardId) => postJson<RileyCard>(`/api/v1/cards/${encodeURIComponent(cardId)}/undo-triage`, {}),
    undoStaffAction: (actionId) =>
      postJson<{ id: string; state: string }>(`/api/v1/automation/actions/${encodeURIComponent(actionId)}/undo`, {}),
    async moveToReplyBox(cardId) {
      await postJson<unknown>(`/api/v1/cards/${encodeURIComponent(cardId)}/use-in-reply-box`, {})
      return getJson<RileyCard>(`/api/v1/cards/${encodeURIComponent(cardId)}`)
    },
    dismissCard: (cardId, reason) => postJson<RileyCard>(`/api/v1/cards/${encodeURIComponent(cardId)}/dismiss`, { reason: reason ?? null }),
    async submitFeedback(feedback) {
      await postJson<unknown>('/api/v1/riley/feedback', {
        target: feedback.target,
        id: feedback.id,
        rating: feedback.rating,
        ...(feedback.note ? { note: feedback.note } : {}),
      })
    },
    getFeedback: (ids) =>
      ids.length
        ? getJson<RileyRating[]>(`/api/v1/riley/feedback/mine?${ids.map((id) => `ids=${encodeURIComponent(id)}`).join('&')}`)
        : Promise.resolve([]),
    cardProgress: (cardId) => getJson<RileyCardProgress | null>(`/api/v1/cards/${encodeURIComponent(cardId)}/progress`),
    investigate(caseId, input): AsyncIterable<InvestigationStreamEvent> {
      return {
        async *[Symbol.asyncIterator]() {
          const { res, done } = await post(`/api/v1/cases/${encodeURIComponent(caseId)}/investigate`, input, {
            accept: 'text/event-stream',
            timeout: STREAM_TIMEOUT_MS,
          })
          yield* streamOf<InvestigationStreamEvent>(res, done)
        },
      }
    },
    getInvestigation: (investigationId) => getJson<StoredInvestigation>(`/api/v1/investigations/${encodeURIComponent(investigationId)}`),
  }
}
